<template>
  <section
    id="boutiques"
    class="boutiques"
  >
    <div class="container">
      <div class="boutiques__head">
        <SectionHead
          :icon="data.boutiques.icon"
          :title="data.boutiques.title"
        />
        <!-- le bouton « Recherche » EST le champ : il s'élargit au clic -->
        <label
          class="search-pill"
          :class="{ 'is-active': searchActive }"
        >
          <input
            v-model="query"
            type="search"
            :placeholder="searchActive ? data.boutiques.searchPlaceholder : data.boutiques.searchLabel"
            :aria-label="data.boutiques.searchLabel"
            @focus="searchFocused = true"
            @blur="searchFocused = false"
            @keydown.esc="clearSearch($event)"
          >
          <button
            v-if="query"
            type="button"
            class="search-pill__clear"
            aria-label="Effacer la recherche"
            @mousedown.prevent
            @click="query = ''"
          >×</button>
          <img
            v-else
            class="search-pill__icon"
            src="/assets/pictos/header/recherche.svg"
            alt=""
          >
        </label>
      </div>

      <div class="boutiques__filters">
        <button
          v-for="cat in data.boutiques.categories"
          :key="cat.id"
          class="boutiques__filter"
          :class="{ 'is-active': cat.id === activeCat }"
          @click="activeCat = cat.id"
        >
          <!-- carré blanc > disque sarcelle > picto blanc (maquette) ; sélectionné :
               carré sarcelle > disque blanc > picto sarcelle. Picto teint en CSS. -->
          <span class="boutiques__filter-icon">
            <span
              class="boutiques__filter-disc"
              :style="{ '--icon': `url(${JSON.stringify(cat.icon)})` }"
            />
          </span>
          <span class="boutiques__filter-label">{{ cat.label }}</span>
        </button>
      </div>

      <div class="boutiques__grid">
        <article
          v-for="shop in filteredShops"
          :key="shop.name"
          class="shop-card"
        >
          <div class="shop-card__logo">
            <img
              v-if="shop.logo"
              :src="shop.logo"
              :alt="shop.name"
            >
            <span v-else>{{ shop.name }}</span>
          </div>
          <p class="shop-card__name">{{ shop.name }}</p>
        </article>
      </div>

      <p
        v-if="!filteredShops.length"
        class="boutiques__empty"
      >Aucune boutique ne correspond à votre recherche.</p>
    </div>
  </section>
</template>

<script>
import SectionHead from '@/components/SectionHead.vue'

// minuscules sans accents : « generale » trouve « Générale d'Optique »
const fold = (s) =>
  String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()

export default {
  name: 'BoutiquesSection',
  components: { SectionHead },
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      activeCat: 'tous',
      query: '',
      searchFocused: false
    }
  },
  computed: {
    // champ ouvert tant qu'il a le focus ou contient une recherche
    searchActive() {
      return this.searchFocused || !!this.query
    },
    filteredShops() {
      let shops = this.data.boutiques.shops
      if (this.activeCat !== 'tous') {
        shops = shops.filter((s) => s.category === this.activeCat)
      }
      const q = fold(this.query)
      if (q) {
        shops = shops.filter((s) => fold(s.name).includes(q))
      }
      return shops
    }
  },
  methods: {
    clearSearch(event) {
      this.query = ''
      event.target.blur()
    }
  }
}
</script>

<style lang="scss" scoped>
// fond clair + motif de courbes en filigrane, sur toute la largeur et très discret
// (courbes plus foncées que le fond, motif calé en haut : relevé sur la maquette)
.boutiques {
  background: $bg-light;
  padding: 64px 0 72px;
  position: relative;
  overflow: hidden;

  &::before {
    @include curves-overlay($curves-light, center top);
  }

  .container {
    position: relative;
  }
}

.boutiques__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
}

// Fermé : identique au bouton sarcelle de la maquette (141 x 40).
// Ouvert (focus ou texte saisi) : fond blanc, s'élargit, loupe -> croix d'effacement.
.search-pill {
  display: inline-flex;
  align-items: center;
  width: 141px;
  height: 40px;
  max-width: 100%;
  border: 1.5px solid $teal;
  border-radius: 8px;
  background: $teal;
  cursor: pointer;
  transition: width 0.25s ease, background 0.2s ease;

  &:hover:not(.is-active) {
    background: darken($teal, 8%);
    border-color: darken($teal, 8%);
  }

  input {
    flex: 1;
    min-width: 0;
    height: 100%;
    padding: 0 0 0 24px;
    border: none;
    background: transparent;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    color: $white;
    cursor: pointer;

    &:focus {
      outline: none;
    }

    &::placeholder {
      color: $white;
      opacity: 1;
    }

    // croix native masquée : on affiche la nôtre, identique partout
    &::-webkit-search-cancel-button {
      -webkit-appearance: none;
    }
  }

  &__icon {
    flex: 0 0 auto;
    width: 15px;
    height: 15px;
    margin: 0 20px 0 8px;
    filter: brightness(0) invert(1);
    transition: filter 0.2s ease;
  }

  &__clear {
    flex: 0 0 auto;
    width: 28px;
    height: 28px;
    margin-right: 8px;
    border: none;
    border-radius: 50%;
    background: rgba($teal, 0.12);
    color: $teal;
    font-size: 18px;
    line-height: 1;
    cursor: pointer;

    &:hover {
      background: rgba($teal, 0.22);
    }
  }

  &.is-active {
    width: 300px;
    background: $white;

    input {
      font-weight: 500;
      color: $ink;
      cursor: text;

      &::placeholder {
        color: rgba($ink, 0.45);
      }
    }

    .search-pill__icon {
      // loupe blanche -> sarcelle
      filter: brightness(0) saturate(100%) invert(47%) sepia(38%) saturate(640%) hue-rotate(155deg) brightness(92%) contrast(88%);
    }
  }
}

.boutiques__filters {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: clamp(12px, 2vw, 26px);
  margin-top: 34px;
}

.boutiques__filter {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 9px;
  background: none;
  border: none;
  padding: 0;
  width: 78px;

  // carré blanc arrondi (78 x 76 px sur la maquette de 2200)
  &-icon {
    width: 60px;
    height: 60px;
    padding: 6px;
    border-radius: 8px;
    background: $white;
    box-shadow: 0 2px 8px rgba($navy, 0.06);
    transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  }

  // disque sarcelle (#3F8CA8 relevé sur la maquette) + picto blanc par masque
  &-disc {
    display: block;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: $teal;
    position: relative;
    transition: background 0.2s ease;

    &::after {
      content: '';
      position: absolute;
      inset: 24%;
      background: $white;
      -webkit-mask: var(--icon) no-repeat center / contain;
      mask: var(--icon) no-repeat center / contain;
      transition: background 0.2s ease;
    }
  }

  &-label {
    font-size: 11px;
    font-weight: 500;
    color: $ink;
    line-height: 1.2;
  }

  &:hover &-icon {
    transform: translateY(-2px);
    box-shadow: 0 6px 14px rgba($navy, 0.16);
  }

  // sélectionné : couleurs inversées
  &.is-active &-icon {
    background: $teal;
  }

  &.is-active &-disc {
    background: $white;

    &::after {
      background: $teal;
    }
  }

  &.is-active &-label {
    color: $teal;
    font-weight: 600;
  }
}

.boutiques__grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 20px 18px;
  margin-top: 40px;
}

.shop-card {
  &__logo {
    background: $white;
    border-radius: 14px;
    aspect-ratio: 1 / 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    box-shadow: 0 2px 8px rgba($navy, 0.05);
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    }

    span {
      font-size: 13px;
      font-weight: 700;
      text-align: center;
      color: $navy;
    }
  }

  &:hover &__logo {
    transform: translateY(-3px);
    box-shadow: 0 10px 22px rgba($navy, 0.14);
  }

  &__name {
    margin-top: 8px;
    font-size: 12px;
    font-weight: 500;
  }
}

.boutiques__empty {
  margin-top: 30px;
  font-size: 14px;
  text-align: center;
  color: rgba($ink, 0.7);
}

@include tablet-down {
  .boutiques__grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

@include mobile {
  .boutiques {
    padding: 48px 0 56px;
  }

  .boutiques__grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px 14px;
  }

  .boutiques__filters {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    justify-items: center;
  }
}
</style>
